import React, { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import "../users/userform.css";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { FiArrowLeft } from "react-icons/fi";
import InternalFooter from "../components/InternalFooter/InternalFooter";
import DateInput from "../components/Forms/DateInput";
import { createStaffEmployment, updateStaffEmployment, getStaffById } from "../../services/StaffService";

const StaffEmploymentForm = () => {
  const navigate = useNavigate();
  const { staffId, employmentId } = useParams();
  const isEditMode = !!employmentId;

  const [loading, setLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});
  const [dataLoaded, setDataLoaded] = useState(!isEditMode);

  const [formData, setFormData] = useState({
    employment_type: "permanent",
    joining_date: "",
    contract_renewal_date: "",
    last_renewed_date: ""
  });

  const notifySuccess = (msg) => toast.success(`✔️ ${msg}`, { position: 'top-right', autoClose: 3000 });
  const notifyError = (msg) => toast.error(`❌ ${msg}`, { position: 'top-right', autoClose: 5000 });

  useEffect(() => {
    if (isEditMode) {
      fetchEmploymentData();
    }
  }, [staffId, employmentId]);

  const fetchEmploymentData = async () => {
    try {
      setLoading(true);
      const res = await getStaffById(staffId);
      const staff = res.data.data || res.data;
      const employment = staff.StaffEmployments?.find(emp => emp.id === parseInt(employmentId));
      
      if (employment) {
        setFormData({
          employment_type: employment.employment_type || "permanent",
          joining_date: employment.joining_date ? new Date(employment.joining_date).toISOString().split('T')[0] : "",
          contract_renewal_date: employment.contract_renewal_date ? new Date(employment.contract_renewal_date).toISOString().split('T')[0] : "",
          last_renewed_date: employment.last_renewed_date ? new Date(employment.last_renewed_date).toISOString().split('T')[0] : ""
        });
      } else {
        notifyError("Employment record not found");
        navigate(`/staff/staffDetails/${staffId}`);
      }
      setDataLoaded(true);
    } catch (error) {
      console.error("Employment fetch error:", error);
      notifyError(error?.response?.data?.message || "Failed to load employment data");
      setDataLoaded(true);
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validate = () => {
    const errs = {};
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) {
      notifyError("Please fill in all required fields");
      return;
    }

    setIsSubmitting(true);
    setLoading(true);
    try {
      if (isEditMode) {
        await updateStaffEmployment(staffId, employmentId, formData);
        notifySuccess("Employment record updated successfully");
      } else {
        await createStaffEmployment(staffId, formData);
        notifySuccess("Employment record added successfully");
      }
      setTimeout(() => navigate(`/staff/staffDetails/${staffId}`), 1500);
    } catch (error) {
      console.error("Submit error:", error);
      notifyError(error?.response?.data?.message || error?.response?.data?.error || `Failed to ${isEditMode ? 'update' : 'add'} employment record`);
    } finally {
      setLoading(false);
      setIsSubmitting(false);
    }
  };

  if (!dataLoaded && loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: '50vh' }}>
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
      </div>
    );
  }

  return (
    <div>
      <button
        className="back-button"
        onClick={() => navigate(`/staff/staffDetails/${staffId}`)}
      >
        <FiArrowLeft style={{ marginRight: "0.5rem" }} />
        Back
      </button>

      <ToastContainer />

      <div className="card p-3 p-md-4 mt-2">
        <h4 className="mb-4">{isEditMode ? "Edit Employment" : "Add Employment"}</h4>
        <form onSubmit={handleSubmit}>
          <div className="row">
            <div className="col-md-6 mb-3">
              <label>Employment Type*</label>
              <select
                name="employment_type"
                value={formData.employment_type}
                onChange={handleChange}
                className="form-control"
                required
              >
                <option value="permanent">Permanent</option>
                <option value="contract based">Contract Based</option>
                <option value="temporary">Temporary</option>
                <option value="intern">Intern</option>
                <option value="part-time">Part-time</option>
                <option value="full-time">Full-time</option>
              </select>
            </div>

            <div className="col-md-6 mb-3">
              <label>Joining Date</label>
              <DateInput
                type="date"
                name="joining_date"
                value={formData.joining_date}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Contract Renewal Date</label>
              <DateInput
                type="date"
                name="contract_renewal_date"
                value={formData.contract_renewal_date}
                onChange={handleChange}
                className="form-control"
              />
            </div>

            <div className="col-md-6 mb-3">
              <label>Last Renewed Date</label>
              <DateInput
                type="date"
                name="last_renewed_date"
                value={formData.last_renewed_date}
                onChange={handleChange}
                className="form-control"
              />
            </div>
          </div>

          <div className="toolbar toolbar-bottom p-2">
            <div className="d-flex flex-row justify-content-end gap-2 w-100">
              <button
                type="button"
                className="btn btn-cancel"
                onClick={() => navigate(`/staff/staffDetails/${staffId}`)}
                disabled={isSubmitting}
                style={{ flex: 1, maxWidth: '200px', minWidth: '100px' }}
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isSubmitting}
                className="btn user-form-btn sw-btn-next"
                style={{ flex: 1, maxWidth: '200px', minWidth: '100px' }}
              >
                {loading ? (
                  <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                ) : null}
                {isEditMode ? "Update" : "Submit"}
              </button>
            </div>
          </div>
        </form>
      </div>

      <InternalFooter />
    </div>
  );
};

export default StaffEmploymentForm;
